import { useState, useEffect } from "react";
import Slider from "react-slick";
import Lightbox from "yet-another-react-lightbox";

// Text Configuration - All text content for easy editing
const textContent = {
  header: {
    title: "Hannah Heckmann",
    nav: ["home", "about", "design", "styling", "craft", "contact"]
  },
  hero: {
    title: "Welcome to Hannah Heckmann’s Portfolio",
    subtitle: "A Journey Through Craft, Design, and Innovation"
  },
  about: {
    title: "About Me",
    subtitle: "Crafting Stories Through Fabric and Vision",
    description: "With a keen eye for detail and a passion for innovative design, Hannah Heckmann brings creativity and functionality together in her projects. Her unique approach to industrial design results in products that are both aesthetically pleasing and user-friendly."
  },
  design: {
    title: "Design",
    description: "Design, Styling, and Creative Direction by Hannah Heckmann",
    casualWear: {
      title: "Casual Wear Project: Forbidden Fruit",
      viewProcess: "View Creative Process"
    },
    joieDeVivre: {
      title: "The Embodiment of Joie de Vivre",
      viewProcess: "View Creative Process"
    },
    alpaca: {
      title: "Alpaca Legacy - Serbian Wool Craft"
    }
  },
  styling: {
    title: "Styling",
    description: "Styling and Creative Direction by Hannah Heckmann",
    runway: {
      title: "Runway Brilliance - Texture Transformation"
    },
    upInSmoke: {
      title: "Up in Smoke",
      credits: {
        models: "Lenard Heckmann, Marija Stankovic, Balou"
      }
    }
  },
  craft: {
    title: "Craft",
    description: "SAVOIR-FAIRE: Delicate fabrics. Fine ingredients. Pure craft.",
    viewDetails: "View More Details",
    processSteps: [
      { id: 1, title: "The Art of Layering in Textile and Taste" },
      { id: 2, title: "Start with patience" },
      { id: 3, title: "Add knowledge" },
      { id: 4, title: "Trust instinct" },
      { id: 5, title: "Embrace contrast" },
      { id: 6, title: "Time is Key" },
      { id: 7, title: "Polishment" },
      { id: 9, title: "Final Touch-Signature" }
    ]
  },
  contact: {
    title: "Let’s Connect",
    description: "Interested in collaboration or custom designs? Reach out to explore possibilities.",
    contactButton: "Contact Me",
    email: "hannah.heckmann.design@gmail.com",
    instagram: "https://instagram.com/hhonneyy_"
  },
  footer: {
    copyright: `© ${new Date().getFullYear()} Hannah Heckmann. All rights reserved.`
  }
};

// Image Paths for Easy Editing
const imagePaths = {
  casualWear: ["/images/casual_wear_1.jpeg","/images/casual_wear_7.png","/images/casual_wear_2.jpeg" ,"/images/casual_wear_8.jpg","/images/casual_wear_3.jpeg","/images/casual_wear_9.jpg","/images/casual_wear_5.jpeg", "/images/casual_wear_6.jpeg", "/images/casual_wear_4.jpeg"],
  casualWearProcess: ["/images/casual_wear_process1.jpg", "/images/casual_wear_process2.jpg","/images/casual_wear_process3.jpg","/images/casual_wear_process5.jpg"],
  joieDeVivre: ["/images/joie_de_vivre_1.jpg", "/images/joie_de_vivre_2.jpg", "/images/joie_de_vivre_3.jpg", "/images/joie_de_vivre_4.jpg", "/images/joie_de_vivre_5.jpg", "/images/joie_de_vivre_6.jpg", "/images/joie_de_vivre_7.jpg", "/images/joie_de_vivre_8.jpg"],
  joieDeVivreProcess: ["/images/joie_de_vivre_process_4.jpg", "/images/joie_de_vivre_process_2.jpg","/images/joie_de_vivre_process_3.jpg","/images/joie_de_vivre_process_1.jpg"],
  alpaca: ["/images/Project_1.jpg", "/images/Project_2.jpg", "/images/Project_3.jpg", "/images/Project_4.jpg", "/images/Project_5.jpg","/images/Project_6.jpg","/images/Project_8.jpg"],
  alpacaProcess: ["/images/Design process 2a.jpg", "/images/design process 3ab.jpg"],
  styling: ["/images/1 Styling for Brand.jpg", "/images/2 Styling for brand.jpg", "/images/3 Styling.jpg", "/images/4 styling.jpg", "/images/5 styling.jpg", "/images/6 styling.jpg", "/images/7 styling.jpg"],
  upInSmoke: ["/images/up_in_smoke_1.jpg", "/images/up_in_smoke_2.jpg", "/images/up_in_smoke_3.jpg", "/images/up_in_smoke_4.jpg", "/images/up_in_smoke_5.jpg", "/images/up_in_smoke_6.jpg", "/images/up_in_smoke_7.jpg", "/images/up_in_smoke_8.jpg", "/images/up_in_smoke_9.jpg", "/images/up_in_smoke_10.jpg"],
  savoirFaire: ["/images/Savoir-faire1.jpg", "/images/Savoir-faire2.jpg", "/images/Savoir-faire3a.jpg", "/images/Savoir-faire3b.jpg", "/images/Savoir-faire4a.jpg", "/images/Savoir-faire4b.jpg", "/images/Savoir-faire5a.jpg", "/images/Savoir-faire5b.jpg", "/images/Savoir-faire6a.jpg", "/images/Savoir-faire6b.jpg", "/images/Savoir-faire7a.jpg", "/images/Savoir-faire7b.jpg", "/images/Savoir-faire8a.jpg", "/images/Savoir-faire8b.jpg", "/images/Savoir-faire10a.jpg", "/images/Savoir-faire10b.jpg"]
};

const NextArrow = (props) => {
  const { className, style, onClick } = props;
  return (
    <div
      className={`${className} absolute top-1/2 right-4 transform -translate-y-1/2 bg-baby-blue text-white p-2 rounded-full cursor-pointer z-10 hover:bg-blush-pink transition`}
      style={{ ...style, display: "block" }}
      onClick={onClick}
    >
      <span></span>
    </div>
  );
};

const PrevArrow = (props) => {
  const { className, style, onClick } = props;
  return (
    <div
      className={`${className} absolute top-1/2 left-4 transform -translate-y-1/2 bg-baby-blue text-white p-2 rounded-full cursor-pointer z-10 hover:bg-blush-pink transition`}
      style={{ ...style, display: "block" }}
      onClick={onClick}
    >
      <span></span>
    </div>
  );
};

const portfolioItems = {
  design: [
    {
      id: 1,
      title: textContent.design.casualWear.title,
      images: imagePaths.casualWear,
      process: { images: imagePaths.casualWearProcess },
      description: ""
    },
    {
      id: 2,
      title: textContent.design.joieDeVivre.title,
      images: imagePaths.joieDeVivre,
      process: { images: imagePaths.joieDeVivreProcess },
      description: ""
    },
    {
      id: 3,
      title: textContent.design.alpaca.title,
      images: imagePaths.alpaca,
      process: { images: imagePaths.alpacaProcess },
      description: ""
    }
  ],
  styling: [
    {
      id: 1,
      title: textContent.styling.runway.title,
      images: imagePaths.styling,
      process: { images: imagePaths.alpacaProcess },
      description: ""
    },
    {
      id: 2,
      title: textContent.styling.upInSmoke.title,
      images: imagePaths.upInSmoke,
      credits: textContent.styling.upInSmoke.credits,
      description: ""
    }
  ],
  craft: [
    {
      id: 1,
      title: textContent.craft.title,
      images: [],
      description: textContent.craft.description,
      process: {
        steps: textContent.craft.processSteps.map((step, index) => ({
          ...step,
          images: [
            imagePaths.savoirFaire[index * 2],
            imagePaths.savoirFaire[index * 2 + 1] || imagePaths.savoirFaire[index * 2]
          ]
        }))
      }
    }
  ]
};

const HannahHeckmann = () => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isProcessOpen, setIsProcessOpen] = useState(null);

  const openLightbox = (item, index = 0, isProcess = false) => {
    setLightboxImages(isProcess ? item.process.images.map(image => ({ src: image })) : item.images.map(image => ({ src: image })));
    setLightboxIndex(index);
    setSelectedItem(item);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setSelectedItem(null);
  };

  const toggleProcess = (processType) => {
    setIsProcessOpen(isProcessOpen === processType ? null : processType);
  };

  const handleOutsideClick = (e) => {
    if (e.target.classList.contains('process-popup')) {
      setIsProcessOpen(null);
    }
  };

  const portfolioSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2 } },
      { breakpoint: 768, settings: { slidesToShow: 1 } },
    ],
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll("section");
      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top >= 0 && rect.top < window.innerHeight * 0.8) {
          section.classList.add("fade-in-visible");
        }
      });
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-cream font-lato text-charcoal relative">
      {/* Header */}
      <header className="fixed top-0 left-0 w-full bg-white shadow-md z-50 border-b border-gray-200">
        <nav className="container mx-auto px-4 py-3 flex justify-between items-center">
          <a href="#home" className="text-2xl font-montserrat text-charcoal hover:text-baby-blue transition-colors font-semibold">
            {textContent.header.title}
          </a>
          <div className="flex items-center gap-4 md:gap-6">
            <div className="hidden md:flex items-center space-x-6">
              {textContent.header.nav.map((item) => (
                <a
                  key={item}
                  href={`#${item}`}
                  className="text-charcoal hover:text-baby-blue transition-colors text-sm font-semibold"
                >
                  {item.charAt(0).toUpperCase() + item.slice(1)}
                </a>
              ))}
            </div>
            <a
              href="https://hannahheckmann.com"
              target="_blank"
              rel="noopener noreferrer"
              className="relative flex items-center justify-center hover:opacity-80 transition-opacity group"
              title="Visit Hannah Heckmann"
            >
              <div className="relative">
                <img
                  src="/images/hana_logo.jpeg"
                  alt="Hana Logo"
                  className="h-10 w-10 md:h-12 md:w-12 rounded-lg object-contain shadow-sm hover:shadow-md transition-shadow"
                />
                <span className="absolute -top-2 -right-2 bg-blush-pink text-charcoal text-xs font-bold px-1.5 py-0.5 rounded-full shadow-md animate-pulse">
                  NEW
                </span>
              </div>
            </a>
            {/* Mobile menu button */}
            <button
              className="md:hidden text-charcoal hover:text-baby-blue transition-colors"
              onClick={() => {
                const nav = document.querySelector('.mobile-nav');
                nav?.classList.toggle('hidden');
              }}
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </nav>
        {/* Mobile Navigation */}
        <div className="mobile-nav hidden md:hidden bg-white border-t border-gray-200">
          <div className="container mx-auto px-4 py-3 flex flex-col space-y-3">
            {textContent.header.nav.map((item) => (
              <a
                key={item}
                href={`#${item}`}
                className="text-charcoal hover:text-baby-blue transition-colors text-sm font-semibold"
                onClick={() => {
                  const nav = document.querySelector('.mobile-nav');
                  nav?.classList.add('hidden');
                }}
              >
                {item.charAt(0).toUpperCase() + item.slice(1)}
              </a>
            ))}
          </div>
        </div>
      </header>

      {/* Hero Section with Video */}
      <section id="home" className="h-screen relative mt-16">
        <video
          autoPlay
          muted
          loop
          className="absolute top-0 left-0 w-full h-full object-cover"
          style={{ objectPosition: "center" }}
        >
          <source src="/videos/savoir-faire.mp4" type="video/mp4" />
          <img
            src={imagePaths.savoirFaire[0]}
            alt="Hero section fallback"
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/50 to-transparent flex items-center">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-6xl font-playfair text-white">{textContent.hero.title}</h1>
            <p className="text-lg md:text-2xl text-white mt-2">{textContent.hero.subtitle}</p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 bg-pastel-yellow fade-in">
        <div className="container mx-auto px-4 flex flex-col items-center text-center">
          <h3 className="text-xl font-playfair text-charcoal mb-2">{textContent.about.title}</h3>
          <h2 className="text-3xl md:text-5xl font-playfair text-charcoal mb-4">{textContent.about.subtitle}</h2>
          <div className="flex flex-col md:flex-row items-center gap-8 max-w-4xl">
            <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4 md:mb-0">
              {textContent.about.description}
            </p>
            <img
              src="/images/hannah-portrait.jpg"
              alt="Hannah Heckmann"
              className="w-auto h-96 object-contain rounded-lg shadow-md"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Design Section */}
      <section id="design" className="py-16 bg-white fade-in">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-playfair text-charcoal mb-8 text-center">{textContent.design.title}</h2>
          <p className="text-center text-gray-700 mb-8">{textContent.design.description}</p>
          {/* The Embodiment of Joie de Vivre */}
          <div className="mb-12">
            <h3 className="text-2xl font-playfair text-charcoal mb-4 text-center">{textContent.design.joieDeVivre.title}</h3>
            <Slider {...portfolioSettings}>
              {portfolioItems.design[1].images.map((image, index) => (
                <div key={index} className="px-2">
                  <div className="bg-cream rounded-lg shadow-md overflow-hidden h-96 flex flex-col">
                    <img
                      src={image}
                      alt={`Joie de Vivre ${index + 1}`}
                      className="w-full h-72 object-contain cursor-pointer"
                      onClick={() => openLightbox(portfolioItems.design[1], index)}
                      loading="lazy"
                    />
                  </div>
                </div>
              ))}
            </Slider>
            <div className="text-center mt-8 relative z-10">
              <button
                onClick={() => toggleProcess('joieDeVivre')}
                className="bg-baby-blue text-charcoal px-6 py-3 rounded-full text-lg font-semibold hover:bg-blush-pink hover:text-white transition-all duration-300 transform hover:scale-105 shadow-md"
              >
                {textContent.design.joieDeVivre.viewProcess}
              </button>
            </div>
          </div>
          {/* Casual Wear Project for Bid’n Fruit */}
          <div className="mb-12">
            <h3 className="text-2xl font-playfair text-charcoal mb-4 text-center">{textContent.design.casualWear.title}</h3>
            <Slider {...portfolioSettings}>
              {portfolioItems.design[0].images.map((image, index) => (
                <div key={index} className="px-2">
                  <div className="bg-cream rounded-lg shadow-md overflow-hidden h-96 flex flex-col">
                    <img
                      src={image}
                      alt={`Casual Wear ${index + 1}`}
                      className="w-full h-72 object-contain cursor-pointer"
                      onClick={() => openLightbox(portfolioItems.design[0], index)}
                      loading="lazy"
                    />
                  </div>
                </div>
              ))}
            </Slider>
            <div className="text-center mt-8 relative z-10">
              <button
                onClick={() => toggleProcess('casualWear')}
                className="bg-baby-blue text-charcoal px-6 py-3 rounded-full text-lg font-semibold hover:bg-blush-pink hover:text-white transition-all duration-300 transform hover:scale-105 shadow-md"
              >
                {textContent.design.casualWear.viewProcess}
              </button>
            </div>
          </div>
          {/* Alpaca Legacy */}
          <div className="mb-12">
            <h3 className="text-2xl font-playfair text-charcoal mb-4 text-center">{textContent.design.alpaca.title}</h3>
            <Slider {...portfolioSettings}>
              {portfolioItems.design[2].images.map((image, index) => (
                <div key={index} className="px-2">
                  <div className="bg-cream rounded-lg shadow-md overflow-hidden h-96 flex flex-col">
                    <img
                      src={image}
                      alt={`Alpaca ${index + 1}`}
                      className="w-full h-72 object-contain cursor-pointer"
                      onClick={() => openLightbox(portfolioItems.design[2], index)}
                      loading="lazy"
                    />
                  </div>
                </div>
              ))}
            </Slider>
          </div>
          {/* Process Popups for Design Section */}
          {isProcessOpen === 'casualWear' && (
            <div className="process-popup fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={handleOutsideClick}>
              <div className="bg-cream p-8 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
                <button
                  onClick={() => toggleProcess('casualWear')}
                  className="absolute top-4 right-4 bg-baby-blue text-charcoal p-2 rounded-full hover:bg-blush-pink hover:text-white transition-all duration-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <h3 className="text-2xl font-playfair text-charcoal mb-6 text-center">{textContent.design.casualWear.title} Process</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {portfolioItems.design[0].process.images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`Casual Wear Process ${index + 1}`}
                      className="w-full h-96 object-contain rounded-lg"
                      loading="lazy"
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          {isProcessOpen === 'joieDeVivre' && (
            <div className="process-popup fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={handleOutsideClick}>
              <div className="bg-cream p-8 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
                <button
                  onClick={() => toggleProcess('joieDeVivre')}
                  className="absolute top-4 right-4 bg-baby-blue text-charcoal p-2 rounded-full hover:bg-blush-pink hover:text-white transition-all duration-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <h3 className="text-2xl font-playfair text-charcoal mb-6 text-center">{textContent.design.joieDeVivre.title} Process</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {portfolioItems.design[1].process.images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`Joie de Vivre Process ${index + 1}`}
                      className="w-full h-96 object-contain rounded-lg"
                      loading="lazy"
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Styling Section */}
      <section id="styling" className="py-16 bg-cream fade-in">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-playfair text-charcoal mb-8 text-center">{textContent.styling.title}</h2>
          <p className="text-center text-gray-700 mb-8">{textContent.styling.description}</p>
          <Slider {...portfolioSettings}>
            {portfolioItems.styling[0].images.map((image, index) => (
              <div key={index} className="px-2">
                <div className="bg-white rounded-lg shadow-md overflow-hidden h-96 flex flex-col">
                  <img
                    src={image}
                    alt={`Styling Look ${index + 1}`}
                    className="w-full h-72 object-contain cursor-pointer"
                    onClick={() => openLightbox(portfolioItems.styling[0], index)}
                    loading="lazy"
                  />
                  <div className="p-4 flex-grow text-center">
                    <p className="text-sm text-gray-700">Look {index + 1}</p>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
          {/* Up in Smoke */}
          <div className="mt-12">
            <h3 className="text-2xl font-playfair text-charcoal mb-4 text-center">{textContent.styling.upInSmoke.title}</h3>
            <p className="text-center text-gray-700 mb-4">
              Creative Direction , Styling, Photography and Makeup by Hannah Heckmann<br />
              Models: {portfolioItems.styling[1].credits.models}
            </p>
            <Slider {...portfolioSettings}>
              {portfolioItems.styling[1].images.map((image, index) => (
                <div key={index} className="px-2">
                  <div className="bg-cream rounded-lg shadow-md overflow-hidden h-96 flex flex-col">
                    <img
                      src={image}
                      alt={`Up in Smoke ${index + 1}`}
                      className="w-full h-72 object-contain cursor-pointer"
                      onClick={() => openLightbox(portfolioItems.styling[1], index)}
                      loading="lazy"
                    />
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </div>
      </section>

      {/* Craft Section */}
      <section id="craft" className="py-16 bg-white fade-in">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-playfair text-charcoal mb-8 text-center">{textContent.craft.title}</h2>
          <p className="text-center text-gray-700 mb-8">{textContent.craft.description}</p>
          <div className="relative mb-8">
            <video
              autoPlay
              muted
              loop
              className="w-auto h-[600px] object-contain rounded-lg mx-auto"
              style={{ maxWidth: "100%" }}
            >
              <source src="/videos/savoir-faire.mp4" type="video/mp4" />
              <img
                src={imagePaths.savoirFaire[0]}
                alt="Savoir-faire process fallback"
                className="w-auto h-[600px] object-contain rounded-lg mx-auto"
                loading="lazy"
              />
            </video>
          </div>
          <div className="text-center mb-8 relative z-10">
            <button
              onClick={() => toggleProcess('savoirFaire')}
              className="bg-baby-blue text-charcoal px-6 py-3 rounded-full text-lg font-semibold hover:bg-blush-pink hover:text-white transition-all duration-300 transform hover:scale-105 shadow-md"
            >
              {textContent.craft.viewDetails}
            </button>
          </div>
          {isProcessOpen === 'savoirFaire' && (
            <div className="process-popup fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={handleOutsideClick}>
              <div className="bg-cream p-8 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
                <button
                  onClick={() => toggleProcess('savoirFaire')}
                  className="absolute top-4 right-4 bg-baby-blue text-charcoal p-2 rounded-full hover:bg-blush-pink hover:text-white transition-all duration-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <h3 className="text-2xl font-playfair text-charcoal mb-6 text-center">{textContent.craft.title} Details</h3>
                <div className="space-y-6">
                  {portfolioItems.craft[0].process.steps.map((step, index) => (
                    <div key={index} className="bg-white rounded-lg p-4 shadow-md">
                      <h4 className="text-xl font-playfair text-charcoal mb-2">{step.title}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <img
                          src={step.images[0]}
                          alt={`${step.title} Text`}
                          className="w-full h-96 object-contain rounded-lg"
                          loading="lazy"
                        />
                        <img
                          src={step.images[1]}
                          alt={`${step.title} Visual`}
                          className="w-full h-96 object-contain rounded-lg"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 bg-cream fade-in">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-playfair text-charcoal mb-4">{textContent.contact.title}</h2>
          <p className="text-base md:text-lg text-gray-700 max-w-2xl mx-auto mb-6">
            {textContent.contact.description}
          </p>
          <a
            href={`mailto:${textContent.contact.email}`}
            className="inline-block bg-baby-blue text-charcoal px-6 py-3 rounded-full text-lg font-semibold hover:bg-blush-pink hover:text-white transition-all duration-300 transform hover:scale-105 shadow-md"
          >
            {textContent.contact.contactButton}
          </a>
          <div className="flex justify-center space-x-4 mt-6">
            <a href={textContent.contact.instagram} className="text-charcoal hover:text-baby-blue">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.326 3.608 1.301.975.975 1.24 2.242 1.301 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.326 2.633-1.301 3.608-.975.975-2.242 1.24-3.608 1.301-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.326-3.608-1.301-.975-.975-1.24-2.242-1.301-3.608-.058-1.266-.07-1.646-.07-4.85s.012-3.584.07-4.85c.062-1.366.326-2.633 1.301-3.608.975.975 2.242-1.24 3.608-1.301 1.266-.058 1.646-.07 4.85-.07zm0-2.163c-3.259 0-3.667.014-4.947.072-1.395.062-2.674.344-3.893 1.563-1.219 1.219-1.501 2.498-1.563 3.893-.058 1.28-.072 1.688-.072 4.947s.014 3.667.072 4.947c.062 1.395.344 2.674 1.563 3.893 1.219 1.219 2.498 1.501 3.893 1.563 1.28.058 1.688.072 4.947.072s3.667-.014 4.947-.072c1.395-.062 2.674-.344 3.893-1.563 1.219-1.219 1.501-2.498 1.563-3.893.058-1.28.072-1.688.072-4.947s-.014-3.667-.072-4.947c-.062-1.395-.344-2.674-1.563-3.893-1.219-1.219-2.498-1.501-3.893-1.563-1.28-.058-1.688-.072-4.947-.072z"/>
                <path d="M12 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zm0 10.162a3.999 3.999 0 110-7.998 3.999 3.999 0 010 7.998z"/>
                <circle cx="18.406" cy="5.594" r="1.44"/>
              </svg>
            </a>
          </div>
          
          {/* Newsletter Section */}
          <div className="mt-12 pt-8 border-t border-gray-300">
            <div className="flex flex-col items-center gap-4">
              <div className="flex items-center gap-3 mb-2">
                <img
                  src="/images/hana_logo.jpeg"
                  alt="Hana Logo"
                  className="h-12 w-12 rounded-lg object-contain"
                />
                <h3 className="text-2xl font-playfair text-charcoal">Join the Newsletter</h3>
              </div>
              <p className="text-base text-gray-700 max-w-xl mx-auto mb-4">
                Stay updated with the latest from Hana. Get exclusive updates, behind-the-scenes content, and special offers delivered to your inbox.
              </p>
              <a
                href="https://hannahheckmann.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-blush-pink text-charcoal px-6 py-3 rounded-full text-lg font-semibold hover:bg-baby-blue hover:text-white transition-all duration-300 transform hover:scale-105 shadow-md"
              >
                Subscribe Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-charcoal py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-white/80 text-sm">{textContent.footer.copyright}</p>
        </div>
      </footer>

      {/* Lightbox */}
      {isLightboxOpen && (
        <Lightbox
          open={isLightboxOpen}
          close={closeLightbox}
          slides={lightboxImages}
          index={lightboxIndex}
          on={{ view: ({ index }) => setLightboxIndex(index) }}
          styles={{
            container: { backgroundColor: "rgba(0, 0, 0, 0.9)" },
            slide: { display: "flex", justifyContent: "center", alignItems: "center" }
          }}
          render={{
            slide: ({ slide }) => (
              <div className="relative w-full h-full flex justify-center items-center">
                <img
                  src={slide.src}
                  alt={selectedItem ? `${selectedItem.title} - ${lightboxIndex < selectedItem.images.length ? "Image" : "Process"} ${lightboxIndex + 1}` : "Preview"}
                  className="max-w-[90vw] max-h-[90vh] object-contain"
                  loading="lazy"
                />
              </div>
            ),
            buttonPrev: () => null,
            buttonNext: () => null,
            buttonClose: () => (
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 bg-baby-blue text-charcoal p-2 rounded-full hover:bg-blush-pink hover:text-white transition-all duration-300 transform hover:scale-105 shadow-md"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )
          }}
        />
      )}
    </div>
  );
};

export default HannahHeckmann;